import { NavLink, useNavigate } from 'react-router-dom';
import { LayoutDashboard, Activity, Users, CreditCard, Shield, FileText, History, Megaphone, Calculator, GraduationCap } from 'lucide-react';
import { ThemeToggle } from '@/components/ui/ThemeToggle';
import { Button } from '@/components/ui/Button';
import { getCurrentUser, signOut } from '@/lib/auth';

// ── Section header ────────────────────────────────────────────────────────────

function NavSection({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="w-full px-2 mb-2">
      <p className="text-xs text-text-secondary uppercase tracking-wider mb-1 px-1">{label}</p>
      <div className="space-y-0.5">{children}</div>
    </div>
  );
}

// ── Nav link ──────────────────────────────────────────────────────────────────

function NavItem({ to, icon: Icon, label }: { to: string; icon: React.ElementType; label: string }) {
  const navLinkClass = ({ isActive }: { isActive: boolean }) =>
    `relative h-9 rounded-lg flex items-center w-full px-3 gap-2.5 transition-all duration-200 ${
      isActive
        ? 'text-primary-500 dark:text-primary-400 bg-primary-100 dark:bg-primary-500/10'
        : 'text-text-muted hover:text-text-primary hover:bg-surface'
    }`;

  return (
    <NavLink to={to} aria-label={label} className={navLinkClass}>
      <Icon className="w-4 h-4 flex-shrink-0" strokeWidth={1.75} />
      <span className="text-sm font-medium whitespace-nowrap">{label}</span>
    </NavLink>
  );
}

// ── AdminNavRail ──────────────────────────────────────────────────────────────

export function AdminNavRail() {
  const navigate = useNavigate();
  const user = getCurrentUser();

  const handleSignOut = async () => {
    await signOut();
    navigate('/login');
  };

  return (
    <nav
      aria-label="Admin navigation"
      className="flex-shrink-0 w-44 bg-surface-elevated border-r border-border flex flex-col items-center pt-4 gap-0 overflow-hidden"
    >
      {/* Monitoring */}
      <NavSection label="Monitoring">
        <NavItem to="/dashboard" icon={LayoutDashboard} label="Dashboard" />
        <NavItem to="/health" icon={Activity} label="Health" />
      </NavSection>

      {/* Operations */}
      <NavSection label="Operations">
        <NavItem to="/users" icon={Users} label="Users" />
        <NavItem to="/coaching" icon={GraduationCap} label="Coaching" />
        <NavItem to="/credits" icon={CreditCard} label="Credits" />
        <NavItem to="/cogs" icon={Calculator} label="COGS" />
        <NavItem to="/admins" icon={Shield} label="Admins" />
      </NavSection>

      {/* Content */}
      <NavSection label="Content">
        <NavItem to="/prompts" icon={FileText} label="Prompts" />
        <NavItem to="/campaigns" icon={Megaphone} label="Campaigns" />
      </NavSection>

      {/* Accountability */}
      <NavSection label="Accountability">
        <NavItem to="/audit" icon={History} label="Audit" />
      </NavSection>

      {/* Spacer */}
      <div className="flex-1" />

      {/* Bottom section: ThemeToggle + user email + sign out */}
      <div className="w-full px-2 pb-1">
        <hr className="border-border mb-2" />
      </div>

      <div className="w-full px-4 flex items-center justify-between pb-1">
        <ThemeToggle />
      </div>

      {user?.email && (
        <div className="w-full px-4 pb-1">
          <p className="text-xs text-text-secondary truncate" title={user.email}>
            {user.email}
          </p>
        </div>
      )}

      <div className="w-full px-2 pb-4">
        <Button
          variant="ghost"
          size="sm"
          className="w-full justify-start text-text-secondary hover:text-text-primary"
          onClick={handleSignOut}
        >
          Sign out
        </Button>
      </div>
    </nav>
  );
}
